import { useRef, useState, useEffect } from "react";
import { Combobox as HeadlessCombobox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";

export function Combobox({
  name,
  value,
  onChange,
  options,
  placeholder = "Select...",
  className = "",
  position = "auto",
  disabled,
}) {
  const [query, setQuery] = useState("");
  const [dropdownStyles, setDropdownStyles] = useState({});
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Filter options based on the query
  const filteredOptions =
    query === ""
      ? options
      : options.filter((opt) =>
          opt.label.toLowerCase().includes(query.toLowerCase())
        );

  // This function calculates and saves the dropdown's position.
  const updateDropdownPosition = () => {
    if (inputRef.current && containerRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      
      // Calculate position relative to the container
      const relativeTop = inputRect.top - containerRect.top;
      const relativeLeft = inputRect.left - containerRect.left;

      // Determine whether to show the dropdown above or below
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;
      const shouldPositionTop = spaceBelow < 200 && spaceAbove > 200;

      // Set styles according to the calculated position
      if (shouldPositionTop) {
        setDropdownStyles({
          top: `${relativeTop}px`,
          left: `${relativeLeft}px`,
          width: `${inputRect.width}px`,
          transform: "translateY(-100%)",
        });
      } else {
        setDropdownStyles({
          top: `${relativeTop + inputRect.height}px`,
          left: `${relativeLeft}px`,
          width: `${inputRect.width}px`,
        });
      }
    }
  };

  const handleChange = (selectedValue) => {
    onChange(selectedValue);
    setQuery("");
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  // This handler ensures the dropdown opens with all options visible on focus/click.
  const handleFocus = () => {
    if (!disabled) {
      setQuery("");
    }
  };

  // --- EFFECTS FOR POSITIONING ---
  // These effects ensure the position is always correct, independent of the dropdown's open state.

  // Initial position calculation on mount
  useEffect(() => {
    updateDropdownPosition();
  }, []);

  // Update position on window resize or scroll
  useEffect(() => {
    const handleEvents = () => {
      updateDropdownPosition();
    };

    window.addEventListener("resize", handleEvents);
    // Use capture for scroll to catch all scroll events, including inside scrollable containers
    window.addEventListener("scroll", handleEvents, true);

    return () => {
      window.removeEventListener("resize", handleEvents);
      window.removeEventListener("scroll", handleEvents, true);
    };
  }, []);

  // Update position if the input element itself is resized
  useEffect(() => {
    if (!inputRef.current) return;

    const resizeObserver = new ResizeObserver(updateDropdownPosition);
    resizeObserver.observe(inputRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    // The container provides the positioning context for the absolute-positioned options
    <div ref={containerRef} className="relative">
      <HeadlessCombobox
        as="div"
        value={value}
        onChange={handleChange}
        name={name}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            {/* Make the entire input area clickable by wrapping it with the Button component */}
            <HeadlessCombobox.Button
              as="div"
              className="relative w-full cursor-pointer data-disabled:cursor-not-allowed"
              disabled={disabled}
            >
              <div className="relative w-full overflow-hidden rounded-md bg-white text-left focus:outline-none sm:text-sm">
                <HeadlessCombobox.Input
                  ref={inputRef}
                  className={clsx(
                    className,
                    "w-full text-left min-h-11 sm:min-h-9 pr-[calc(--spacing(7)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pl-[calc(--spacing(3)-1px)]",
                    "py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
                    disabled
                      ? "bg-gray-100 text-zinc-500 cursor-not-allowed"
                      : "bg-white text-gray-900 cursor-pointer data-disabled:cursor-not-allowed"
                  )}
                  displayValue={(optionValue) => {
                    const selected = options.find((opt) => opt.value === optionValue);
                    return selected?.label || "";
                  }}
                  onChange={(event) => setQuery(event.target.value)}
                  onFocus={handleFocus}
                  placeholder={placeholder}
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="size-5 sm:size-4 text-zinc-500" />
                </div>
              </div>
            </HeadlessCombobox.Button>

            {open && (
              <HeadlessCombobox.Options
                className={clsx(
                  "absolute z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
                )}
                style={dropdownStyles}
              >
                {filteredOptions.length === 0 ? (
                  <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                    No records found
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <HeadlessCombobox.Option
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      className={({ active }) =>
                        clsx(
                          "relative cursor-default select-none py-2 pl-4 pr-10",
                          active ? "bg-indigo-600 text-white" : "text-gray-900"
                        )
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={clsx(
                              "block truncate",
                              selected ? "font-medium" : "font-normal"
                            )}
                          >
                            {option.label}
                          </span>
                          {selected && (
                            <span
                              className={clsx(
                                "absolute inset-y-0 right-0 flex items-center pr-3",
                                active ? "text-white" : "text-indigo-600"
                              )}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </HeadlessCombobox.Option>
                  ))
                )}
              </HeadlessCombobox.Options>
            )}
          </>
        )}
      </HeadlessCombobox>
    </div>
  );
}